import { Component, type ErrorInfo, type ReactNode } from 'react'
import { Card } from '@/components/ui/card'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
}

export const ErrorFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background p-4">
    <Card variant="dark" className="text-center max-w-md mx-auto p-8">
      <div className="text-5xl font-bold text-[#C0FD72] mb-4">!</div>
      <h1 className="text-2xl font-semibold text-white mb-2">Something went wrong</h1>
      <p className="text-gray-400 mb-8">
        An unexpected error occurred. You can go back to the previous page or reload to try again.
      </p>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <button
          onClick={() => window.history.back()}
          className="px-5 py-2 rounded-md border border-white/20 bg-white/5 hover:bg-white/10 text-white transition-colors"
        >
          Go back
        </button>
        <button
          onClick={() => window.location.reload()}
          className="px-5 py-2 rounded-md bg-[#C0FD72] text-black hover:bg-[#d2ff9c] transition-colors"
        >
          Reload page
        </button>
      </div>
    </Card>
  </div>
)

class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false }

  static getDerivedStateFromError(): State {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Uncaught error:', error, info)
  }

  render() {
    if (this.state.hasError) {
      return <ErrorFallback />
    }
    return this.props.children
  }
}

export default ErrorBoundary
